import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config.ts';
import { User, UserRole } from '../types';
import { Permission, migrateRoleToPermissions, hasPermission, hasAnyPermission } from '../types/permissions.ts';

// Permission simulation state
const SIMULATION_STORAGE_KEY = 'permission_simulation';
let simulatedPermissions: Permission[] | null = null;

// Load simulation state from localStorage on module load
try {
  const stored = localStorage.getItem(SIMULATION_STORAGE_KEY);
  if (stored) {
    simulatedPermissions = JSON.parse(stored);
  }
} catch (e) {
  // Ignore errors
}

interface AuthContextType {
  currentUser: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, permissions: Permission[]) => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  // Simulation functions
  startSimulation: (permissions: Permission[]) => void;
  stopSimulation: () => void;
  isSimulating: boolean;
  simulationInfo: { permissions: Permission[]; preset: string } | null;
  logout: () => Promise<void>;
  updateUserProfile: (displayName: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSimulating, setIsSimulating] = useState(!!simulatedPermissions);
  const [simulationInfo, setSimulationInfo] = useState<{ permissions: Permission[]; preset: string } | null>(
    simulatedPermissions ? { permissions: simulatedPermissions, preset: 'custom' } : null
  );

  const login = async (email: string, password: string): Promise<void> => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (
    email: string,
    password: string,
    displayName: string,
    permissions: Permission[]
  ): Promise<void> => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name
      await updateProfile(user, { displayName });

      // Create user document in Firestore
      const userData: Omit<User, 'uid'> = {
        email: user.email!,
        displayName,
        permissions,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(doc(db, 'users', user.uid), {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const updateUserProfile = async (displayName: string): Promise<void> => {
    if (!firebaseUser) throw new Error('No user logged in');
    
    try {
      await updateProfile(firebaseUser, { displayName });
      
      // Update user document in Firestore
      await setDoc(
        doc(db, 'users', firebaseUser.uid),
        {
          displayName,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const fetchUserData = async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check if user requires password reset on first login
        if (userData.requiresPasswordReset) {
          // Show alert to user about password reset requirement
          alert('This is your first login. Please check your email for a password reset link to set up your permanent password.');
        }
        
        // Handle migration from role-based to permission-based system
        let userPermissions: Permission[] = userData.permissions || [];
        let needsMigration = false;
        
        // Migrate if no permissions but has role, OR if permissions are empty/invalid
        if (userData.role && (!userData.permissions || userData.permissions.length === 0)) {
          userPermissions = migrateRoleToPermissions(userData.role);
          needsMigration = true;
        }
        
        // Force migration for admin users to ensure they get admin permissions
        if (userData.role === 'admin' && !userData.permissions?.includes('admin')) {
          userPermissions = migrateRoleToPermissions('admin');
          needsMigration = true;
        }
        
        // Update the user document with migrated permissions if needed
        if (needsMigration) {
          await setDoc(
            doc(db, 'users', firebaseUser.uid),
            {
              permissions: userPermissions,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
        
        return {
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || userData.displayName,
          permissions: userPermissions,
          employeeId: userData.employeeId,
          contractorId: userData.contractorId,
          createdAt: userData.createdAt?.toDate() || new Date(),
          updatedAt: userData.updatedAt?.toDate() || new Date(),
          role: userData.role, // Keep for migration purposes
        };
      } else {
        // If user document doesn't exist, create a default one
        const defaultPermissions = migrateRoleToPermissions('field_electrician'); // Default permissions
        const defaultUserData: Omit<User, 'uid'> = {
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || '',
          permissions: defaultPermissions,
          createdAt: new Date(),
          updatedAt: new Date(),
          role: 'field_electrician', // Keep for migration
        };

        await setDoc(doc(db, 'users', firebaseUser.uid), {
          ...defaultUserData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        return {
          uid: firebaseUser.uid,
          ...defaultUserData,
        };
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      
      if (firebaseUser) {
        setFirebaseUser(firebaseUser);
        const userData = await fetchUserData(firebaseUser);
        setCurrentUser(userData);
      } else {
        setFirebaseUser(null);
        setCurrentUser(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const checkPermission = (permission: Permission): boolean => {
    if (!currentUser) return false;
    // Use simulated permissions if simulation is active
    const permissionsToCheck = simulatedPermissions || currentUser.permissions;
    return hasPermission(permissionsToCheck, permission);
  };

  const checkAnyPermission = (permissions: Permission[]): boolean => {
    if (!currentUser) return false;
    // Use simulated permissions if simulation is active
    const permissionsToCheck = simulatedPermissions || currentUser.permissions;
    return hasAnyPermission(permissionsToCheck, permissions);
  };

  // Simulation functions
  const startSimulation = (permissions: Permission[]) => {
    simulatedPermissions = permissions;
    localStorage.setItem(SIMULATION_STORAGE_KEY, JSON.stringify(permissions));
    setIsSimulating(true);
    setSimulationInfo({ permissions, preset: 'custom' });
  };

  const stopSimulation = () => {
    simulatedPermissions = null;
    localStorage.removeItem(SIMULATION_STORAGE_KEY);
    setIsSimulating(false);
    setSimulationInfo(null);
  };

  const value: AuthContextType = {
    currentUser,
    firebaseUser,
    loading,
    login,
    register,
    logout,
    updateUserProfile,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    startSimulation,
    stopSimulation,
    isSimulating,
    simulationInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
